import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import {
  SCRATCH_REVEAL_THRESHOLD,
  createScratchGrid,
  markStroke,
  scratchCoverage,
  type ScratchGrid,
} from '@shared';
import { sfx } from '../../lib/audio';
import styles from './ScratchCard.module.css';

export interface ScratchSurfaceHandle {
  /** Clear the whole foil — what "Reveal" does, and what a keyboard user reaches for. */
  clearAll: () => void;
}

interface ScratchSurfaceProps {
  /** The realm's cover art, or null for the drawn foil. */
  coverSrc: string | null;
  /** Whether the foil can be rubbed off yet. Before a card is bought it is sealed. */
  active: boolean;
  /** Called once, when enough has been scratched (or `clearAll` ran). */
  onReveal: () => void;
  /** Called with 0…1 as the foil comes off, for the "keep going" hint. */
  onProgress?: (coverage: number) => void;
}

/** Brush width in CSS px — a thumb's worth, so a phone reveals in a few swipes. */
const BRUSH = 34;

function token(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/** The drawn foil: the realm's colours, a sheen, and a pattern a coin would catch. */
function paintFoil(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const gradient = ctx.createLinearGradient(0, 0, w, h);
  gradient.addColorStop(0, token('--brand-400', '#a78bfa'));
  gradient.addColorStop(0.5, token('--brand-600', '#7c3aed'));
  gradient.addColorStop(1, token('--accent-gold', '#fbbf24'));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);

  ctx.globalAlpha = 0.16;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  for (let x = -h; x < w + h; x += 26) {
    ctx.beginPath();
    ctx.moveTo(x, h);
    ctx.lineTo(x + h, 0);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  const sheen = ctx.createLinearGradient(0, 0, w, 0);
  sheen.addColorStop(0, 'rgba(255,255,255,0)');
  sheen.addColorStop(0.45, 'rgba(255,255,255,0.28)');
  sheen.addColorStop(0.55, 'rgba(255,255,255,0.28)');
  sheen.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `800 ${Math.round(h * 0.17)}px system-ui, sans-serif`;
  ctx.fillText('SCRATCH', w / 2, h * 0.46);
  ctx.font = `600 ${Math.round(h * 0.07)}px system-ui, sans-serif`;
  ctx.fillText('rub here to reveal', w / 2, h * 0.64);
}

/**
 * The scratch-off foil: a canvas laid over the prize.
 *
 * Rubbing it erases pixels (`destination-out`), and how much has gone is tracked
 * on a coarse grid (`@shared/scratch`) rather than read back from the canvas — a
 * cover hosted elsewhere taints it and makes `getImageData` throw. Pointer events
 * cover mouse, touch and pen with one code path, and `touch-action: none` keeps a
 * swipe from scrolling the page out from under the card.
 *
 * The prize beneath is already decided by the server when a card is bought; the
 * foil only controls when the player *sees* it, never what it is.
 */
export const ScratchSurface = forwardRef<ScratchSurfaceHandle, ScratchSurfaceProps>(
  function ScratchSurface({ coverSrc, active, onReveal, onProgress }, ref) {
    const canvas = useRef<HTMLCanvasElement>(null);
    const grid = useRef<ScratchGrid>(createScratchGrid());
    const last = useRef<{ x: number; y: number } | null>(null);
    const revealed = useRef(false);
    const lastTick = useRef(0);
    const onRevealRef = useRef(onReveal);
    onRevealRef.current = onReveal;

    // (Re)paint the foil whenever the cover changes or a new card begins.
    useEffect(() => {
      const element = canvas.current;
      if (!element) return;
      const ctx = element.getContext('2d');
      if (!ctx) return;

      const rect = element.getBoundingClientRect();
      // Back the canvas at the device's pixel ratio so the foil text, sheen and
      // cover art are crisp on retina/3x phones instead of soft. Capped at 3:
      // past that the backing store grows faster than the eye gains, and this is
      // an erasable surface repainted on every card.
      const ratio = Math.min(window.devicePixelRatio || 1, 3);
      element.width = Math.max(1, Math.round(rect.width * ratio));
      element.height = Math.max(1, Math.round(rect.height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, rect.width, rect.height);

      grid.current = createScratchGrid();
      revealed.current = false;
      last.current = null;

      if (!coverSrc) {
        paintFoil(ctx, rect.width, rect.height);
        return;
      }

      paintFoil(ctx, rect.width, rect.height);
      let cancelled = false;
      const image = new Image();
      image.onload = () => {
        if (cancelled || revealed.current) return;
        // Cover-fit the realm's art, then keep the foil's sheen over it.
        const scale = Math.max(rect.width / image.width, rect.height / image.height);
        const dw = image.width * scale;
        const dh = image.height * scale;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(image, (rect.width - dw) / 2, (rect.height - dh) / 2, dw, dh);
      };
      image.src = coverSrc;
      return () => {
        cancelled = true;
      };
    }, [coverSrc, active]);

    function finish() {
      if (revealed.current) return;
      revealed.current = true;
      onProgress?.(1);
      onRevealRef.current();
    }

    useImperativeHandle(ref, () => ({ clearAll: finish }));

    function point(event: React.PointerEvent<HTMLCanvasElement>) {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        w: rect.width,
        h: rect.height,
      };
    }

    function scratchTo(event: React.PointerEvent<HTMLCanvasElement>) {
      const ctx = canvas.current?.getContext('2d');
      const from = last.current;
      if (!ctx || !from || revealed.current) return;
      const { x, y, w, h } = point(event);

      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = BRUSH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(x, y);
      ctx.stroke();

      markStroke(grid.current, from.x, from.y, x, y, BRUSH / 2, w, h);
      last.current = { x, y };

      const coverage = scratchCoverage(grid.current);
      onProgress?.(coverage);
      if (coverage >= SCRATCH_REVEAL_THRESHOLD) {
        finish();
        return;
      }
      const now = performance.now();
      if (now - lastTick.current > 110) {
        lastTick.current = now;
        sfx.play('tick');
      }
    }

    return (
      <canvas
        ref={canvas}
        className={`${styles.foil} ${active ? styles.foilActive : ''}`}
        aria-hidden="true"
        onPointerDown={(event) => {
          if (!active || revealed.current) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          const { x, y } = point(event);
          last.current = { x, y };
          scratchTo(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 0 && event.pointerType === 'mouse') return;
          scratchTo(event);
        }}
        onPointerUp={() => {
          last.current = null;
        }}
        onPointerCancel={() => {
          last.current = null;
        }}
      />
    );
  },
);
